import { apiRequest } from '@/lib/api';
import type { ProjectPaymentItem, ProjectStatus } from '@/lib/projects-clients';
import type { ApprovalDeliveryMethod } from '@/lib/tickets';

export type BillingPaymentStatusFilter = 'unpaid' | 'partially_paid' | 'paid' | 'all';
export type BillingPaymentStatus = 'UNPAID' | 'PARTIALLY_PAID' | 'PAID';
export type BillingDateRange =
  | 'this_month'
  | 'last_month'
  | 'last_30_days'
  | 'last_90_days'
  | 'last_6_months'
  | 'custom';

export type PaymentFollowupHistoryItem = {
  id: string;
  sentAt: string;
  deliveryMethod: ApprovalDeliveryMethod | 'FOLLOW_UP';
  recipientEmail?: string | null;
  recipientPhone?: string | null;
  status?: string | null;
  sentChannels?: string[];
  failedChannels?: unknown;
  smsConsentConfirmed?: boolean | null;
  smsConsentConfirmedAt?: string | null;
};

export type BillingProjectItem = {
  projectId: string;
  projectName: string;
  projectCode?: string | null;
  clientId?: string | null;
  clientName?: string | null;
  clientEmail?: string | null;
  clientPhone?: string | null;
  projectStatus: ProjectStatus;
  archivedAt?: string | null;
  approvedAmount: number;
  paidAmount: number;
  balance: number;
  paymentStatus: BillingPaymentStatus;
  collectedInRange: number;
  lastPaymentFollowupAt?: string | null;
  paymentFollowupHistory?: PaymentFollowupHistoryItem[];
  lastPayment: {
    date: string;
    amount: number;
  } | null;
};

export type BillingExportOverview = {
  currencyCode: string;
  filters: {
    dateRange: BillingDateRange;
    startDate: string;
    endDate: string;
    projectId?: string | null;
    status: BillingPaymentStatusFilter;
    includeArchived: boolean;
  };
  kpis: {
    receivable: number;
    collected: number;
    outstandingProjects: number;
  };
  items: BillingProjectItem[];
  payments: Array<{
    id: string;
    amount: number;
    paymentDate: string;
    method: ProjectPaymentMethod;
    note?: string | null;
    projectId: string;
    projectName: string;
    projectCode?: string | null;
    clientId?: string | null;
    clientName?: string | null;
  }>;
};

export type ProjectLedgerResponse = {
  project: {
    id: string;
    name: string;
    code?: string | null;
    status: ProjectStatus;
    archivedAt?: string | null;
    lastPaymentFollowupAt?: string | null;
    paymentFollowupHistory?: PaymentFollowupHistoryItem[];
    client?: {
      id: string;
      name: string;
      email?: string | null;
      phone?: string | null;
    } | null;
  };
  summary: {
    approvedAmount: number;
    paidAmount: number;
    balance: number;
    paymentStatus: BillingPaymentStatus;
    collectedInRange: number;
  };
  approvedTickets: Array<{
    id: string;
    ticketNumber: string;
    approvedDate: string;
    value: number;
    pdfUrl?: string | null;
  }>;
  payments: ProjectPaymentItem[];
};

export type BillingExportResult = {
  fileName: string;
  url: string;
};

export function fetchBillingExportOverview(filters: {
  dateRange: BillingDateRange;
  status: BillingPaymentStatusFilter;
  projectId?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
  includeArchived?: boolean;
}) {
  const query = new URLSearchParams();
  query.set('dateRange', filters.dateRange);
  query.set('status', filters.status);
  if (filters.projectId) query.set('projectId', filters.projectId);
  if (filters.search?.trim()) query.set('search', filters.search.trim());
  if (filters.startDate) query.set('startDate', filters.startDate);
  if (filters.endDate) query.set('endDate', filters.endDate);
  if (filters.includeArchived !== undefined) {
    query.set('includeArchived', String(filters.includeArchived));
  }

  return apiRequest<BillingExportOverview>(`/billing-exports?${query.toString()}`, {
    method: 'GET',
  });
}

export function fetchProjectBillingLedger(projectId: string, filters?: {
  dateRange?: BillingDateRange;
  startDate?: string;
  endDate?: string;
}) {
  const query = new URLSearchParams();
  if (filters?.dateRange) query.set('dateRange', filters.dateRange);
  if (filters?.startDate) query.set('startDate', filters.startDate);
  if (filters?.endDate) query.set('endDate', filters.endDate);

  return apiRequest<ProjectLedgerResponse>(
    `/billing-exports/projects/${projectId}/ledger${query.toString() ? `?${query.toString()}` : ''}`,
    { method: 'GET' },
  );
}

export function exportProjectStatementPdf(projectId: string) {
  return apiRequest<BillingExportResult>(`/billing-exports/projects/${projectId}/statement-pdf`, {
    method: 'GET',
  });
}

export function exportProjectPaymentsCsv(projectId: string) {
  return apiRequest<BillingExportResult>(`/billing-exports/projects/${projectId}/payments-csv`, {
    method: 'GET',
  });
}

export function sendProjectPaymentFollowup(
  projectId: string,
  payload: {
    deliveryMethod: ApprovalDeliveryMethod;
    smsConsentConfirmed?: boolean;
  },
) {
  return apiRequest<{
    success: boolean;
    deliveryMethod: ApprovalDeliveryMethod;
    recipientEmail: string | null;
    recipientPhone: string | null;
    sentChannels: Array<'email' | 'sms'>;
    failedChannels: Array<{ channel: 'email' | 'sms'; message: string }>;
    subject: string;
    followedUpAt?: string;
    historyItem?: PaymentFollowupHistoryItem;
  }>(
    `/billing-exports/projects/${projectId}/payment-followup`,
    { method: 'POST', body: payload },
  );
}

export function exportCollectedPaymentsPdf(filters: {
  dateRange: BillingDateRange;
  startDate?: string;
  endDate?: string;
}) {
  const query = new URLSearchParams();
  query.set('dateRange', filters.dateRange);
  if (filters.startDate) query.set('startDate', filters.startDate);
  if (filters.endDate) query.set('endDate', filters.endDate);

  return apiRequest<BillingExportResult>(`/billing-exports/collected-payments-pdf?${query.toString()}`, {
    method: 'GET',
  });
}

export function exportReceivablePaymentsPdf(filters: {
  dateRange: BillingDateRange;
  startDate?: string;
  endDate?: string;
}) {
  const query = new URLSearchParams();
  query.set('dateRange', filters.dateRange);
  if (filters.startDate) query.set('startDate', filters.startDate);
  if (filters.endDate) query.set('endDate', filters.endDate);

  return apiRequest<BillingExportResult>(`/billing-exports/receivable-payments-pdf?${query.toString()}`, {
    method: 'GET',
  });
}
